import { ProductId } from '../value-objects/ProductId';
import { Money } from '../value-objects/Money';

export interface ProductAttribute {
  key: string;
  value: string;
  label: string;
}

export class OrderItem {
  private constructor(
    private readonly _variantId: string,
    private readonly _productId: ProductId,
    private readonly _sku: string,
    private readonly _productName: string,
    private readonly _price: Money,
    private _quantity: number,
    private readonly _attributes: ProductAttribute[],
    private _note: string = '',
  ) {}

  static create(
    variantId: string,
    productId: ProductId,
    sku: string,
    productName: string,
    price: Money,
    quantity: number,
    attributes: ProductAttribute[] = [],
    note: string = '',
  ): OrderItem {
    if (quantity <= 0) {
      throw new Error('Order item quantity must be greater than zero');
    }

    if (!variantId?.trim()) {
      throw new Error('Variant ID is required');
    }

    if (!sku?.trim()) {
      throw new Error('SKU is required');
    }

    if (!productName?.trim()) {
      throw new Error('Product name is required');
    }

    return new OrderItem(
      variantId.trim(),
      productId,
      sku.trim(),
      productName.trim(),
      price,
      quantity,
      [...attributes],
      note.trim(),
    );
  }

  getVariantId(): string {
    return this._variantId;
  }

  getProductId(): ProductId {
    return this._productId;
  }

  getSku(): string {
    return this._sku;
  }

  getProductName(): string {
    return this._productName;
  }

  getPrice(): Money {
    return this._price;
  }

  getQuantity(): number {
    return this._quantity;
  }

  getAttributes(): ProductAttribute[] {
    return [...this._attributes];
  }

  getNote(): string {
    return this._note;
  }

  getSubtotal(): Money {
    return this._price.multiplyByFactor(this._quantity);
  }

  updateQuantity(newQuantity: number): OrderItem {
    if (newQuantity <= 0) {
      throw new Error('Quantity must be greater than zero');
    }

    return new OrderItem(
      this._variantId,
      this._productId,
      this._sku,
      this._productName,
      this._price,
      newQuantity,
      this._attributes,
      this._note,
    );
  }

  updateNote(newNote: string): OrderItem {
    return new OrderItem(
      this._variantId,
      this._productId,
      this._sku,
      this._productName,
      this._price,
      this._quantity,
      this._attributes,
      newNote.trim(),
    );
  }

  getDisplayName(): string {
    if (this._attributes.length === 0) {
      return this._productName;
    }

    const attributeLabels = this._attributes.map((attr) => attr.label).join(', ');
    return `${this._productName} (${attributeLabels})`;
  }

  equals(other: OrderItem): boolean {
    return (
      this._variantId === other._variantId && this._productId.isEqualTo(other._productId) && this._sku === other._sku
    );
  }
}
